import React from 'react';

import { getCopilotDrawerWidthBounds } from '../../constants';

type UseCopilotDrawerResizerParams = {
  copilotDrawerWidth: number;
  setCopilotDrawerWidth: (width: number) => void;
};

type UseCopilotDrawerResizerResult = {
  isResizingDrawer: boolean;
  minCopilotDrawerWidth: number;
  maxCopilotDrawerWidth: number;
  handleResizePointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  handleResizeHandleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};

export const useCopilotDrawerResizer = ({
  copilotDrawerWidth,
  setCopilotDrawerWidth,
}: UseCopilotDrawerResizerParams): UseCopilotDrawerResizerResult => {
  // Tracks drag origin so width can be computed from pointer delta.
  const drawerResizeSessionRef = React.useRef<{ startX: number; startWidth: number } | null>(null);
  const [isResizingDrawer, setIsResizingDrawer] = React.useState(false);

  const viewportWidth = typeof window === 'undefined' ? copilotDrawerWidth : window.innerWidth;
  const { minWidth: minCopilotDrawerWidth, maxWidth: maxCopilotDrawerWidth } =
    getCopilotDrawerWidthBounds(viewportWidth);

  React.useEffect(() => {
    if (!isResizingDrawer) {
      return undefined;
    }

    // Pointer listeners are attached to window so drag keeps working outside the handle.
    const handlePointerMove = (event: PointerEvent) => {
      const resizeSession = drawerResizeSessionRef.current;
      if (!resizeSession) {
        return;
      }

      const nextWidth = resizeSession.startWidth + (resizeSession.startX - event.clientX);
      setCopilotDrawerWidth(nextWidth);
    };

    const handlePointerUp = () => {
      drawerResizeSessionRef.current = null;
      setIsResizingDrawer(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isResizingDrawer, setCopilotDrawerWidth]);

  React.useEffect(() => {
    if (!isResizingDrawer) {
      document.documentElement.style.removeProperty('--copilot-drawer-layout-transition-duration');
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      return undefined;
    }

    // Disable transitions/selection while dragging to keep resize smooth and predictable.
    document.documentElement.style.setProperty('--copilot-drawer-layout-transition-duration', '0ms');
    document.body.style.setProperty('cursor', 'col-resize');
    document.body.style.setProperty('user-select', 'none');

    return () => {
      document.documentElement.style.removeProperty('--copilot-drawer-layout-transition-duration');
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };
  }, [isResizingDrawer]);

  const handleResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    drawerResizeSessionRef.current = {
      startX: event.clientX,
      startWidth: copilotDrawerWidth,
    };
    setIsResizingDrawer(true);
  };

  const handleResizeHandleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setCopilotDrawerWidth(copilotDrawerWidth + 24);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setCopilotDrawerWidth(copilotDrawerWidth - 24);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setCopilotDrawerWidth(minCopilotDrawerWidth);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setCopilotDrawerWidth(maxCopilotDrawerWidth);
    }
  };

  return {
    isResizingDrawer,
    minCopilotDrawerWidth,
    maxCopilotDrawerWidth,
    handleResizePointerDown,
    handleResizeHandleKeyDown,
  };
};
